/**
 * Rate Limit Handler
 *
 * Utility for handling API rate limit errors (HTTP 429) with automatic retry
 * and user-friendly error messages.
 */

/**
 * Error class for rate limit errors with retry information
 */
export class RateLimitError extends Error {
  readonly retryAfterSeconds: number | null;
  readonly isRateLimitError = true;

  constructor(message: string, retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /**
   * Get a user-friendly message about the rate limit
   */
  getUserFriendlyMessage(): string {
    if (this.retryAfterSeconds !== null && this.retryAfterSeconds > 0) {
      const minutes = Math.ceil(this.retryAfterSeconds / 60);
      if (minutes <= 1) {
        return `Rate limit reached. Please wait about ${String(this.retryAfterSeconds)} seconds before trying again.`;
      }
      return `Rate limit reached. Please wait about ${String(minutes)} minute${minutes > 1 ? 's' : ''} before trying again.`;
    }
    return 'Rate limit reached. Please wait a moment before trying again.';
  }
}

/**
 * Parse rate limit information from Anthropic API error response
 */
export interface RateLimitInfo {
  isRateLimitError: boolean;
  retryAfterSeconds: number | null;
  message: string;
}

/**
 * Parse rate limit information from response headers and body
 */
export function parseRateLimitError(
  status: number,
  headers: Headers | Record<string, string>,
  body?: { error?: { message?: string; type?: string } }
): RateLimitInfo {
  const isRateLimitError = status === 429;

  if (!isRateLimitError) {
    return {
      isRateLimitError: false,
      retryAfterSeconds: null,
      message: body?.error?.message || `API error: ${String(status)}`,
    };
  }

  // Try to get retry-after from headers
  let retryAfterSeconds: number | null = null;

  // Handle both Headers object and plain object
  const getHeader = (name: string): string | null => {
    if (headers instanceof Headers) {
      return headers.get(name);
    }
    return headers[name] || headers[name.toLowerCase()] || null;
  };

  const retryAfterHeader = getHeader('retry-after');
  if (retryAfterHeader) {
    const parsed = parseInt(retryAfterHeader, 10);
    if (!isNaN(parsed)) {
      retryAfterSeconds = parsed;
    }
  }

  // If no retry-after header, try to parse from error message
  if (retryAfterSeconds === null && body?.error?.message) {
    // Common patterns in Anthropic error messages
    const secondsMatch = /(\d+)\s*second/i.exec(body.error.message);
    const minutesMatch = /(\d+)\s*minute/i.exec(body.error.message);

    if (secondsMatch && secondsMatch[1]) {
      retryAfterSeconds = parseInt(secondsMatch[1], 10);
    } else if (minutesMatch && minutesMatch[1]) {
      retryAfterSeconds = parseInt(minutesMatch[1], 10) * 60;
    }
  }

  // Default to 60 seconds if we couldn't determine the wait time
  if (retryAfterSeconds === null) {
    retryAfterSeconds = 60;
  }

  const message =
    body?.error?.message || 'Rate limit exceeded. Please try again later.';

  return {
    isRateLimitError: true,
    retryAfterSeconds,
    message,
  };
}

/**
 * Configuration for fetch with retry
 */
export interface RetryConfig {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  retryOn429Only?: boolean;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 60000,
  backoffMultiplier: 2,
  retryOn429Only: true,
};

/**
 * Sleep for a specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fetch with automatic retry for rate limit errors
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param retryConfig - Retry configuration
 * @param onRetry - Optional callback when retrying (receives attempt number and delay)
 * @returns The fetch response
 * @throws RateLimitError if all retries fail due to rate limiting
 */
export async function fetchWithRateLimitRetry(
  url: string,
  options: RequestInit,
  retryConfig: RetryConfig = {},
  onRetry?: (attempt: number, delayMs: number, retryAfterSeconds: number | null) => void
): Promise<Response> {
  const config = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  let lastError: RateLimitError | null = null;
  let attempt = 0;

  while (attempt <= config.maxRetries) {
    try {
      const response = await fetch(url, options);

      // If successful or not a rate limit error (when retryOn429Only is true), return
      if (response.ok) {
        return response;
      }

      // Check for rate limit
      if (response.status === 429) {
        const body = await response.json().catch(() => ({})) as { error?: { message?: string } };
        const rateLimitInfo = parseRateLimitError(response.status, response.headers, body);

        lastError = new RateLimitError(rateLimitInfo.message, rateLimitInfo.retryAfterSeconds);

        // If we have retries left, wait and retry
        if (attempt < config.maxRetries) {
          // Calculate delay: use retry-after header if available, otherwise exponential backoff
          let delayMs: number;
          if (rateLimitInfo.retryAfterSeconds !== null) {
            // Use the server-suggested delay, but cap it
            delayMs = Math.min(rateLimitInfo.retryAfterSeconds * 1000, config.maxDelayMs);
          } else {
            // Exponential backoff
            delayMs = Math.min(
              config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt),
              config.maxDelayMs
            );
          }

          if (onRetry) {
            onRetry(attempt + 1, delayMs, rateLimitInfo.retryAfterSeconds);
          }

          await sleep(delayMs);
          attempt++;
          continue;
        }

        // No more retries, throw the rate limit error
        throw lastError;
      }

      // If not retrying on non-429 errors, just return the response
      if (config.retryOn429Only) {
        return response;
      }

      // For non-429 errors when retryOn429Only is false, could implement generic retry
      // For now, just return the response
      return response;
    } catch (error) {
      // If it's our RateLimitError, re-throw it
      if (error instanceof RateLimitError) {
        throw error;
      }

      // Network errors - could retry these too
      throw error;
    }
  }

  // Should not reach here, but just in case
  if (lastError) {
    throw lastError;
  }

  throw new Error('Unexpected error in fetchWithRateLimitRetry');
}

/**
 * Check if an error is a rate limit error
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return (
    error instanceof RateLimitError ||
    (error instanceof Error && 'isRateLimitError' in error && (error as RateLimitError).isRateLimitError)
  );
}

/**
 * Format a rate limit error for display to users
 */
export function formatRateLimitErrorForUser(error: unknown): string {
  if (isRateLimitError(error)) {
    return error.getUserFriendlyMessage();
  }

  // Check if it's a generic error with rate limit message patterns
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('rate limit') || message.includes('429') || message.includes('too many requests')) {
      return 'Rate limit reached. Please wait a moment before trying again.';
    }
  }

  return error instanceof Error ? error.message : 'An unknown error occurred';
}
